import type { ComponentProps } from 'react';

import { Badge, type BadgeProps } from './badge';

/**
 * 도메인 상태 배지 — D1 §3 / D2 §2.3.
 *
 * `Badge`는 색을 고를 수 있지만, 그래서 실수할 수 있다.
 * 이 컴포넌트는 **상태 이름만 받고 색을 고르지 못하게 한다.** 같은 상태가
 * 화면마다 다른 색으로 나오는 일을 구조적으로 막는 것이 목적이다.
 *
 * 새 상태가 필요하면 여기 표에 등록한다. 표 밖의 색을 쓰지 않는다.
 */
export type Status =
  // 과제 · 공고
  | 'ongoing'
  | 'ended'
  | 'open'
  | 'closed'
  // 확인 흐름 (P3 — 사람이 최종 확인한다)
  | 'confirmed'
  | 'pending'
  | 'approved'
  | 'rejected'
  // 읽기 상태
  | 'read'
  | 'unread'
  // 마감 임박도
  | 'urgent'
  | 'soon'
  | 'relaxed';

const STATUS: Record<Status, { tone: NonNullable<BadgeProps['tone']>; label: string }> = {
  ongoing: { tone: 'good', label: '진행중' },
  ended: { tone: 'neutral', label: '종료' },
  open: { tone: 'good', label: '모집중' },
  closed: { tone: 'neutral', label: '마감' },

  confirmed: { tone: 'good', label: '확정' },
  pending: { tone: 'seal', label: '확인 필요' },
  approved: { tone: 'good', label: '승인됨' },
  rejected: { tone: 'danger', label: '거절됨' },

  read: { tone: 'good', label: '읽음' },
  unread: { tone: 'seal', label: '미열람' },

  urgent: { tone: 'seal', label: '긴급' },
  soon: { tone: 'gold', label: '임박' },
  relaxed: { tone: 'good', label: '여유' },
};

export interface StatusBadgeProps
  extends Omit<ComponentProps<'span'>, 'children'>, Pick<BadgeProps, 'shape'> {
  status: Status;
  /** 기본 문구를 바꿀 때만 쓴다. 색은 바꿀 수 없다. */
  children?: React.ReactNode;
}

export function StatusBadge({ status, children, ...props }: StatusBadgeProps) {
  const { tone, label } = STATUS[status];
  return (
    <Badge tone={tone} {...props}>
      {children ?? label}
    </Badge>
  );
}
